import moment from 'moment';
import { interval, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export enum ReactionType {
  LIKE = 'like',
}

export type SalesPersonAction =
  | 'opportunity-closed-lost'
  | 'opportunity-closed-won'
  | 'opportunity-reopened'
  | 'opportunity-updated'
  | 'archived-business'
  | 'unarchived-business'
  | 'other'
  | 'inbound-message';

export interface SalesActivityBasicInfo {
  salesActivityId: string;
  salesPersonAction: SalesPersonAction;
  opportunityId?: string;
}

export interface SalesActivityInterface extends SalesActivityBasicInfo {
  notes: string;
  previousSalesStatus: string;
  salesStatus: string;
  salesPersonName: string;
  salesPersonPhoto: string;
  closedLostReason: string;
  connected: boolean;
  audioKey?: string;
}

export interface SalesActivityReaction {
  reactionUserName: string;
  reactionUserId: string;
  reactionType: ReactionType;
}

export class SalesActivityComment {
  formattedCreatedDate: Observable<string>;
  constructor(
    public commentUserName: string,
    public commentProfilePictureUrl: string,
    public commentText: string,
    public commentCreatedDateTime: string,
  ) {
    this.commentProfilePictureUrl = this.commentProfilePictureUrl || '/static/images/Profile.svg';
    const createdDate = this.commentCreatedDateTime;
    this.formattedCreatedDate = interval(1000).pipe(
      map(() => {
        return moment.utc(createdDate).local().fromNow();
      }),
    );
  }
}

export class SalesActivity implements SalesActivityInterface {
  static STATUS_ENUM = {
    READY_TO_SELL: 'ready-to-sell',
    IN_PROGRESS: 'in-progress',
    CLOSED_WON: 'closed-won',
    CLOSED_LOST: 'closed-lost',
    FOLLOW_UP_NEEDED: 'follow-up-needed',
  };

  EDIT_TIME_LIMIT_IN_WEEKS = 1;
  ACTIONS: { [key: string]: string } = {
    'replied-by-email': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.REPLIED_BY_EMAIL'),
    'email-sent': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.SENT_EMAIL_TO'),
    'email-received': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.RECEIVED_EMAIL'),
    'inbound-call': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.HANDLED_INBOUND_CALL'),
    'outbound-call': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OUTBOUND_CALL'),
    'presentation-booked': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.PRESENTATION_BOOKED'),
    'presentation-done': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.PRESENTATION_DONE'),
    meeting: this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.MEETING'),
    'opportunity-created': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OPPORTUNITY_CREATED'),
    'opportunity-closed-won': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OPPORTUNITY_CLOSED_WON'),
    'opportunity-closed-lost': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OPPORTUNITY_CLOSED_LOST'),
    'opportunity-updated': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OPPORTUNITY_UPDATED'),
    'archived-business': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.ARCHIVED_BUSINESS'),
    'unarchived-business': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.UNARCHIVED_BUSINESS'),
    'opportunity-reopened': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.OPPORTUNITY_REOPENED'),
  };

  ACTION_VERBS: { [key: string]: string } = {
    'replied-by-email': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.REPLIED_BY_EMAIL'),
    'email-sent': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.EMAIL_SENT'),
    'email-received': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.EMAIL_RECEIVED'),
    'inbound-call': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.INBOUND_CALL'),
    'outbound-call': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OUTBOUND_CALL'),
    'presentation-booked': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.PRESENTATION_BOOKED'),
    'presentation-done': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.PRESENTATION_DONE'),
    meeting: this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.MEETING'),
    'opportunity-created': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_CREATED'),
    'opportunity-closed-won': this.translate.instant(
      'SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_CLOSED_WON',
    ),
    'opportunity-closed-lost': this.translate.instant(
      'SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_CLOSED_LOST',
    ),
    'opportunity-updated': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_UPDATED'),
    'archived-business': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.ARCHIVED_BUSINESS'),
    'unarchived-business': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.UNARCHIVED_BUSINESS'),
    'opportunity-reopened': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_REOPENED'),
    'account-created': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.ACCOUNT_CREATED'),
    'inbound-message': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.INBOUND_MESSAGE'),
  };

  STATUS: { [key: string]: string } = {
    'ready-to-sell': this.translate.instant('SALES_ACTIVITY.STATUS.READY_TO_SELL'),
    'in-progress': this.translate.instant('SALES_ACTIVITY.STATUS.IN_PROGRESS'),
    'closed-won': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_CLOSED_WON'),
    'closed-lost': this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTION_VERBS.OPPORTUNITY_CLOSED_LOST'),
    'follow-up-needed': this.translate.instant('SALES_ACTIVITY.STATUS.FOLLOW_UP_NEEDED'),
  };

  NOT_DELETABLE_ACTIONS: Array<string> = [
    'opportunity-created',
    'opportunity-closed-won',
    'opportunity-closed-lost',
    'archived-business',
    'unarchived-business',
    'opportunity-reopened',
  ];

  CLOSED_LOST_REASONS: { [key: string]: string } = {
    price: this.translate.instant('SALES_OPPORTUNITIES.CLOSE.CLOSED_LOST.PRICE'),
    'no-budget': this.translate.instant('SALES_OPPORTUNITIES.CLOSE.CLOSED_LOST.NO_BUDGET'),
    'lost-to-competitor': this.translate.instant('SALES_OPPORTUNITIES.CLOSE.CLOSED_LOST.LOST_TO_COMPETITOR'),
    'not-ready': this.translate.instant('SALES_OPPORTUNITIES.CLOSE.CLOSED_LOST.NOT_READY'),
    other: this.translate.instant('SALES_OPPORTUNITIES.CLOSE.CLOSED_LOST.OTHER'),
  };

  public formattedStatus = '';
  public formattedClosedLostReason = '';
  public formattedPreviousStatus = '';
  public formattedAction = '';
  public formattedActionVerb: string;
  public createdDate: string;
  public lastEditDatetime: string;
  public formattedDate: Observable<string>;
  public shouldShowAction = false;
  public likes: SalesActivityReaction[];
  public reactions: SalesActivityReaction[];
  public comments: SalesActivityComment[];
  public recentComments: (commentsLeft: number) => SalesActivityComment[];
  public restOfComments: (commentsLeft: number) => SalesActivityComment[];
  public isDeletable: boolean;
  public productPrice: number;

  public static buildSalesActivityFromRaw(rawItem: any, translate: TranslateService): SalesActivity {
    return new SalesActivity(
      rawItem.notes,
      rawItem.closed_lost_reason,
      rawItem.previous_sales_status,
      rawItem.sales_status,
      rawItem.sales_person_action,
      rawItem.custom_action_name,
      rawItem.salesperson_name,
      rawItem.salesperson_photo,
      rawItem.company_name,
      rawItem.event_datetime,
      rawItem.like_count,
      rawItem.reactions,
      rawItem.comments,
      rawItem.connected,
      rawItem.account_group_id,
      rawItem.sales_record_id,
      translate,
      rawItem.product_name,
      rawItem.product_price,
      rawItem.opportunity_id,
      rawItem.salesperson_id,
      rawItem.last_edit_datetime,
      rawItem.audio_key,
    );
  }

  public static shouldMarkConnected(action: string): boolean {
    return action === 'inbound-call' || action === 'meeting' || action === 'email-received';
  }

  constructor(
    public notes: string,
    public closedLostReason: string,
    public previousSalesStatus: string,
    public salesStatus: string,
    public salesPersonAction: SalesPersonAction,
    public customActionName: string,
    public salesPersonName: string,
    public salesPersonPhoto: string,
    public companyName: string,
    public date: string,
    public likeCount: number,
    reactions: {
      reaction_type: string;
      reaction_user_id: string;
      reaction_user_name: string;
    }[] = [],
    comments: {
      user_name: string;
      profile_picture_url: string;
      text: string;
      created: string;
    }[] = [],
    public connected: boolean,
    public accountGroupId: string,
    public salesActivityId: string,
    public translate: TranslateService,
    public productName?: string,
    productPrice?: number,
    public opportunityId?: string,
    public salespersonId?: string,
    public lastEdit?: string,
    public audioKey?: string,
  ) {
    this.productPrice = productPrice !== null ? productPrice : 0;
    this.createdDate = date;
    this.lastEditDatetime = lastEdit;
    this.reactions = reactions.map((r) => {
      return <SalesActivityReaction>{
        reactionUserName: r.reaction_user_name,
        reactionUserId: r.reaction_user_id,
        reactionType: r.reaction_type,
      };
    });
    this.comments = comments.map((c) => {
      return new SalesActivityComment(c.user_name, c.profile_picture_url, c.text, c.created);
    });
    this.recentComments = function (commentsLeft: number): SalesActivityComment[] {
      return this.comments.slice(this.comments.length - (3 + commentsLeft));
    };
    this.restOfComments = function (commentsLeft: number): SalesActivityComment[] {
      return this.comments.slice(0, this.comments.length - (3 + commentsLeft));
    };
    this.setLikes();
    this.salesPersonPhoto = salesPersonPhoto || '/static/images/Profile.svg';

    this.formattedStatus = this.salesStatus ? this.STATUS[this.salesStatus] || 'Unknown' : '';
    this.formattedClosedLostReason = this.closedLostReason
      ? this.CLOSED_LOST_REASONS[this.closedLostReason] || 'Other'
      : '';
    this.formattedPreviousStatus = this.previousSalesStatus ? this.STATUS[this.previousSalesStatus] || 'Unknown' : '';
    const verb = this.salesPersonAction
      ? this.ACTIONS[this.salesPersonAction] || this.translate.instant('SALES_ACTIVITY.SALES_ACTIVITY_ACTIONS.UNKNOWN')
      : null;
    this.formattedAction = verb ? verb : '';
    this.formattedActionVerb = this.salesPersonAction ? this.ACTION_VERBS[this.salesPersonAction] || 'Unknown' : '';
    this.isDeletable = !this.NOT_DELETABLE_ACTIONS.includes(this.salesPersonAction);
    this.shouldShowAction =
      !!this.salesPersonAction &&
      (this.salesStatus === 'follow-up-needed' ||
        this.salesStatus === 'in-progress' ||
        this.salesPersonAction === 'archived-business' ||
        this.salesPersonAction === 'unarchived-business' ||
        this.salesPersonAction === 'opportunity-reopened');
    //noinspection TypeScriptUnresolvedVariable
    this.formattedDate = interval(1000).pipe(
      map(() => {
        return moment.utc(date).local().fromNow();
      }),
    );
    this.connected = connected;
  }

  public addReaction(reaction: SalesActivityReaction): void {
    this.reactions.push(reaction);
    this.setLikes();
  }

  public addComment(userName: string, profilePictureUrl: string, commentText: string): void {
    this.comments.push(new SalesActivityComment(userName, profilePictureUrl, commentText, undefined));
  }

  public hasReactedTo(reactionType: ReactionType, salesPersonId: string): boolean {
    const userReaction = this.reactions.filter((reaction) => {
      return reaction.reactionUserId === salesPersonId && reaction.reactionType === reactionType;
    });
    return userReaction.length > 0;
  }

  public reactionText(
    reactionType: ReactionType,
    salesPersonId: string,
    translate: TranslateService,
  ): Observable<string> {
    let reactions = this.getReactionsByType(reactionType);
    let salesPersonName;

    if (reactions.length === 0) {
      return translate.stream('SALES_ACTIVITY.REACTION_TEXT.BE_THE_FIRST');
    }
    if (this.hasReactedTo(reactionType, salesPersonId)) {
      // Get salesperson.  If not found, then it's your reaction.
      if (reactions.length === 1) {
        salesPersonName = reactions[0].reactionUserName;
      } else {
        salesPersonName = 'You';
      }
      reactions = reactions.filter((reaction) => {
        return reaction.reactionUserId !== salesPersonId;
      });
    } else {
      salesPersonName = reactions[0].reactionUserName;
      const user_id = reactions[0].reactionUserId;
      reactions = this.reactions.filter((reaction) => {
        return reaction.reactionUserId !== user_id;
      });
    }
    let name;
    if (reactions.length > 0) {
      name = reactions[0].reactionUserName;
    }

    // These are reactions that do not include the impersonated salesperson.  At this point if the reactions count
    // is 0 then it means the impersonated salesperson was the only one to react on this.
    if (reactions.length === 0) {
      return of(salesPersonName);
    } else if (reactions.length === 1) {
      return translate.stream('SALES_ACTIVITY.REACTION_TEXT.A_SALESPERSON_AND_ONE_OTHER', {
        person: salesPersonName,
        otherPerson: name,
      });
    } else if (reactions.length > 1) {
      return translate.stream('SALES_ACTIVITY.REACTION_TEXT.A_SALESPERSON_AND_MORE_THAN_ONE_OTHER', {
        person: salesPersonName,
        otherPerson: name,
        numberOfOtherPeople: reactions.length - 1,
      });
    }
  }

  public reactionTooltip(reactionType: ReactionType): string {
    const reactions = this.getReactionsByType(reactionType);
    const reactionCount = reactions.length;

    let tooltip = '';

    reactions.forEach((reaction, index) => {
      if (index !== 0 && index === reactionCount - 1) {
        tooltip += ' and ';
      } else if (index > 0) {
        tooltip += ', ';
      }

      tooltip += reaction.reactionUserName;
    });

    return tooltip;
  }

  private getReactionsByType(reactionType: ReactionType): SalesActivityReaction[] {
    return this.reactions.filter((reaction) => {
      return reaction.reactionType === reactionType;
    });
  }

  private setLikes(): void {
    this.likes = this.reactions.filter((r) => {
      return r.reactionType === ReactionType.LIKE;
    });
  }

  public isEditable(salespersonId: string, isImpersonation: boolean): boolean {
    if (!this.isDeletable || salespersonId !== this.salespersonId || isImpersonation) {
      return false;
    }
    const minMoment = moment(Date.now()).subtract(this.EDIT_TIME_LIMIT_IN_WEEKS, 'week');
    const currentMoment = moment(this.createdDate);
    return minMoment <= currentMoment;
  }
}
